import { useEffect, useRef } from "react";

// Small modal that stands in for window.confirm — themed, i18n-friendly,
// keyboard-accessible (Esc cancels, focus is trapped on the confirm button).
// Renders at z-[90] so it always stacks above the Sheet component
// (z-[80]) — otherwise a destructive confirm raised from inside a
// drawer would render behind it and look broken.
//
// <ConfirmDialog
//   open={...}
//   title="Supprimer le projet ?"
//   message="Cette action est irréversible."
//   confirmLabel="Supprimer"
//   cancelLabel="Annuler"
//   tone="danger"               // "danger" | "primary"
//   onConfirm={...}
//   onCancel={...}
// />
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  onConfirm,
  onCancel,
  busy = false,
}) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmClass =
    tone === "danger"
      ? "bg-danger hover:brightness-110 focus:ring-danger/40"
      : "bg-primary hover:brightness-110 focus:ring-primary/40";

  return (
    // Backdrop click closes the dialog; Escape is handled by the
    // useEffect above so keyboard users still get the dismiss path.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div className="w-full max-w-sm rounded-lg bg-surface shadow-xl border border-border">
        <div className="p-5">
          <h3
            id="confirm-dialog-title"
            className="text-base font-semibold text-text"
          >
            {title}
          </h3>
          {message ? (
            <p className="mt-2 text-sm text-secondText">{message}</p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-border text-text bg-surface hover:bg-surfaceAlt transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/40"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
